import React, {useEffect, useMemo} from 'react'
import Field from '../../../components/field'
import WarrantyHeader from './warranty-header'
import WarrantyFooter from './warranty-footer'
import {useWarrantyContext, WARRANTY_MODES} from '../util/warranty-context'
import {useForm, useWatch} from 'react-hook-form'
import WarrantyFormContainer from './warranty-form-container'
import WarrantyStepContainer from './warranty-step-container'
import {FormattedMessage, useIntl} from 'react-intl'
import {Flex, Link, Text} from '@chakra-ui/react'

const ProductSelection = () => {
    const {
        data,
        mode,
        config: {warranties},
        onSubmit
    } = useWarrantyContext()
    const {formatMessage} = useIntl()

    const {groupedWarranties, groupOptions} = useMemo(() => {
        const grouped = warranties
            ? warranties
                  .filter((item) => {
                      return item?.scope?.indexOf(mode) >= 0
                  })
                  .reduce((acc, item) => {
                      if (!acc[item.group]) {
                          acc[item.group] = []
                      }
                      acc[item.group].push(item.name)
                      return acc
                  }, {})
            : []

        const sorted = Object.fromEntries(
            Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b))
        )

        const groupOptions = [
            {
                value: '',
                label: formatMessage({
                    id: 'warranty.producttype.missingvalue',
                    defaultMessage: 'Please select'
                })
            },
            ...Object.keys(sorted).map((key) => ({
                value: key,
                label: key
            }))
        ]

        return {groupedWarranties: sorted, groupOptions}
    }, [formatMessage, warranties, mode])

    const form = useForm({
        defaultValues: {
            userInputSelectedProductGroup:
                data?.userInputSelectedProductGroup ??
                warranties?.userInputSelectedProductGroup ??
                '',
            userInputSelectedProductName:
                data?.userInputSelectedProductName ?? warranties?.userInputSelectedProductName ?? ''
        }
    })

    const selectedGroup = useWatch({
        control: form.control,
        name: 'userInputSelectedProductGroup',
        exact: true
    })

    const productOptions = useMemo(() => {
        if (!selectedGroup || !groupedWarranties[selectedGroup]) return []
        return groupedWarranties[selectedGroup].map((product) => ({
            value: product,
            label: product
        }))
    }, [selectedGroup, groupedWarranties])

    const termsAndConditionsUrl = useMemo(() => {
        if (!selectedGroup) return null
        return warranties.find(
            (warranty) => warranty.group === selectedGroup && warranty.termsAndConditions
        )?.termsAndConditions
    }, [selectedGroup, warranties])

    useEffect(() => {
        const selectedGroupProducts = groupedWarranties[selectedGroup] || []
        const selectedName = form.getValues('userInputSelectedProductName')
        if (!selectedGroupProducts.includes(selectedName)) {
            form.resetField('userInputSelectedProductName', {defaultValue: ''})
        }
    }, [selectedGroup, groupedWarranties, form])

    const requiredMessage = formatMessage({
        id: 'global.error.field_is_required',
        defaultMessage: 'This field is required.'
    })

    return (
        <form noValidate onSubmit={form.handleSubmit(onSubmit)}>
            <WarrantyStepContainer>
                <WarrantyHeader>
                    {mode === WARRANTY_MODES.CPSIA
                        ? formatMessage({
                              id: 'cpsia.header.step1',
                              defaultMessage: 'PRODUCT REGISTRATION FOR SAFETY ALERT OR RECALL ONLY'
                          })
                        : formatMessage({
                              id: 'warranty_portal.step.product.header',
                              defaultMessage: 'Register your product and get extended warranty'
                          })}
                </WarrantyHeader>

                {mode === WARRANTY_MODES.CPSIA && (
                    <Text>
                        <FormattedMessage
                            id="cpsia.header.step1.text"
                            defaultMessage="We will use the information provided to contact you only if there is a safety alert or recall for this product.<br></br><br></br>We will not sell, rent, or share your personal information. To register your product, please complete the form below, using the information found on the card attached to your product.<br></br><br></br>If you have any difficulties registering your product, or if you have any questions, please contact us at:<br></br><br></br>Tel: 1.877.978.6553<br></br><br></br>E-mail: <email>info-USA@stokke.com</email><br></br><br></br>"
                            values={{
                                br: () => <br />,
                                email: (chunks) => <Link href={`mailto:${chunks}`}>{chunks}</Link>
                            }}
                        />
                    </Text>
                )}

                <Text textStyle={'Regular XLarge'}>
                    {formatMessage({
                        id: 'warrantyportal.subheader.step1',
                        defaultMessage: 'Please select your product below'
                    })}
                </Text>

                {groupedWarranties && (
                    <WarrantyFormContainer>
                        <Flex direction="column" alignItems="stretch" gap={4}>
                            <Field
                                name="userInputSelectedProductGroup"
                                type="select"
                                label={formatMessage({
                                    id: 'warranty_portal.step.product.group',
                                    defaultMessage: 'Product Group'
                                })}
                                options={groupOptions}
                                control={form.control}
                                inputProps={{
                                    isRequired: true
                                }}
                                rules={{required: requiredMessage}}
                                error={form.formState.errors.userInputSelectedProductGroup}
                            />
                            {termsAndConditionsUrl ? (
                                <Link
                                    isExternal
                                    alignSelf="flex-start"
                                    href={termsAndConditionsUrl}
                                    size="sm"
                                >
                                    <FormattedMessage
                                        id="footer.link.terms_conditions"
                                        defaultMessage="Terms and Conditions"
                                    />
                                </Link>
                            ) : null}
                        </Flex>
                        <Field
                            name="userInputSelectedProductName"
                            type="select"
                            label={formatMessage({
                                id: 'warranty_portal.step.product.name',
                                defaultMessage: 'Product name'
                            })}
                            options={productOptions}
                            control={form.control}
                            inputProps={{
                                isRequired: true,
                                placeholder: selectedGroup
                                    ? formatMessage({
                                          id: 'warranty_portal.step.product.select_product',
                                          defaultMessage: 'Select a product'
                                      })
                                    : formatMessage({
                                          id: 'warranty_portal.step.product.select_group',
                                          defaultMessage: 'Select a group first'
                                      }),
                                isDisabled: !selectedGroup
                            }}
                            rules={{required: requiredMessage}}
                            error={form.formState.errors.userInputSelectedProductName}
                        />
                    </WarrantyFormContainer>
                )}

                <WarrantyFooter />
            </WarrantyStepContainer>
        </form>
    )
}

export default ProductSelection
